'use client';

import classNames from 'classnames';
import React, { ReactNode, useState, useTransition } from 'react';
import { ChevronRight } from 'react-bootstrap-icons';

import { loadRelatedDetail } from './RelatedEntityDetailAction';
import { Spinner } from './wrapped';

import styles from '@/styles/RelatedEntityRow.module.scss';

export interface RelatedEntityRowProps {
  /** Adjacent entity to load on expand. */
  entityId: string
  /** Total columns, so the panel cell can span the full row. */
  colSpan: number
  /** The row's featured-value cells (server-rendered). */
  children: ReactNode
}

/** A relationship-table row that lazily expands to the adjacent entity's
 * factsheet: a server action renders it (with lineage) and streams back an RSC
 * payload. The panel row mounts only while open, so it leaves no doubled
 * separator in the bordered table. */
export function RelatedEntityRow({ entityId, colSpan, children }: RelatedEntityRowProps) {
  const [open, setOpen] = useState(false);
  const [node, setNode] = useState<ReactNode | null>(null);
  const [isPending, startTransition] = useTransition();

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && node === null && !isPending) {
      startTransition(async () => {
        const detail = await loadRelatedDetail(entityId);
        setNode(detail);
      });
    }
  };

  return (
    <>
      <tr>
        {children}
        <td className={styles.triggerCell}>
          <span
            className={styles.trigger}
            role="button"
            tabIndex={0}
            aria-expanded={open}
            onClick={toggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle();
              }
            }}
          >
            Details
            <ChevronRight className={classNames(styles.chevron, { [styles.chevronOpen]: open })} />
          </span>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={colSpan} className={styles.panelCell}>
            <div className={styles.panelGrid}>
              <div className={styles.panelInner}>
                {isPending && node === null
                  ? <Spinner animation="border" size="sm" />
                  : node}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
